import type { CSSProperties } from "react";
import type React from "react";
import { isValidElement } from "react";

import dayjs from "dayjs";
import { styled } from "styled-components";

import { shortMonthAndYear } from "@similarweb/pro-logic-components";
import type { StrictColor } from "@similarweb/styles";
import { colorsPalettes } from "@similarweb/styles";
import { ChangeValue } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { i18nFilter } from "app/filters/i18nFilter";
import { percentageSignFilter } from "app/filters/ngFilters";

import { CHART_COLORS } from "../config/consts";

// Snapshot timestamps from the API are UTC; format in UTC so the same month is shown
// regardless of the viewer's local timezone (DRI-10166).
const timestampFormatter = (timestamp: number) => dayjs.utc(timestamp).format(shortMonthAndYear);
const changeFormatter = percentageSignFilter();

const Container = styled(Box)`
    display: grid;
    grid-template-columns: 1.2fr repeat(3, 1fr);
    grid-template-rows: auto 1px auto;
    column-gap: 4px;
    row-gap: 4px;
    padding: 4px;
    width: 300px;
`;

const RowDivider = styled.div`
    grid-column: 1 / -1;
    grid-row: 2;
    background: ${colorsPalettes.carbon[100]};
    height: 100%;
`;

type ItemProps = {
    color?: StrictColor;
    children?: React.ReactNode;
    $justifyContent?: CSSProperties["justifyContent"];
};

const HeaderItem = ({ color, children }: ItemProps) => (
    <Flex
        justifyContent="flex-end"
        alignContent="center"
    >
        <Text
            fontVariant="subtitle2"
            color={color}
        >
            {children}
        </Text>
    </Flex>
);

const RowItem = ({ color, children, $justifyContent }: ItemProps) => {
    const content = isValidElement(children) ? (
        children
    ) : (
        <Text
            data-automation="overview-graph-yoy-tooltip-value"
            fontVariant="tooltip"
            color={color}
        >
            {children}
        </Text>
    );

    return (
        <Flex
            justifyContent={$justifyContent || "flex-end"}
            alignContent="center"
        >
            {content}
        </Flex>
    );
};

type PeriodOverPeriodTooltipProps = {
    previousPeriodTimeStamp: number;
    currentPeriodTimeStamp: number;
    previousPeriodValue: number;
    currentPeriodValue: number;
    change: number;
    valueFormatter: (value: number) => string;
};

export const PeriodOverPeriodTooltip = ({
    previousPeriodTimeStamp,
    currentPeriodTimeStamp,
    previousPeriodValue,
    currentPeriodValue,
    change,
    valueFormatter,
}: PeriodOverPeriodTooltipProps) => {
    const t = i18nFilter();

    return (
        <Container>
            {/* HEADER */}
            <Box />
            <HeaderItem color={CHART_COLORS.PREVIOUS_PERIOD}>{timestampFormatter(previousPeriodTimeStamp)}</HeaderItem>
            <HeaderItem color={CHART_COLORS.CURRENT_PERIOD}>{timestampFormatter(currentPeriodTimeStamp)}</HeaderItem>
            <HeaderItem>{t("common.tooltip.change")}</HeaderItem>
            {/* DIVIDER */}
            <RowDivider />
            {/* ROWS */}
            <RowItem
                color={colorsPalettes.carbon[200]}
                $justifyContent="flex-start"
            >
                {t("websources.total")}
            </RowItem>
            <RowItem>{valueFormatter(previousPeriodValue)}</RowItem>
            <RowItem>{valueFormatter(currentPeriodValue)}</RowItem>
            <RowItem>
                <Flex alignSelf="center">
                    <ChangeValue
                        value={changeFormatter(change)}
                        unsigned={change === 0}
                        isDecrease={change < 0}
                    />
                </Flex>
            </RowItem>
        </Container>
    );
};
